"use client";
import { GQLExternalLinkBlock, GQLInternalLinkBlock } from "graphql/generated-types";
import Link from "next/link";
import { useMedia, useLockBodyScroll } from "react-use";
import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import clsx from "clsx";
import UBOLogo from "@/svgs/ubo-logo.svg";
import { usePathname, useRouter, useParams } from "next/navigation";
import ChevronRight from "@/svgs/chevron-right.svg";
import { useSession } from "utils/hooks";

type NavProps = {
	menu: (GQLExternalLinkBlock[] | GQLInternalLinkBlock[]) | null;
	dashboardSlug: string;
	loggedIn: boolean;
	name: string;
};

const Nav: React.FC<NavProps> = ({ menu, dashboardSlug, loggedIn, name }) => {
	// const { data: dashboardSlug } = useGetDashboardSlugQuery();
	const isMobile = useMedia("(max-width: 1023px)", false);

	const path = usePathname();
	const params = useParams();
	// const [loggedIn, setLoggedIn] = useState(false);loggedIn
	const [isMenuOpen, setIsMenuOpen] = useState(false);

	useLockBodyScroll(isMenuOpen);
	const pathname = usePathname();

	useEffect(() => {
		if (isMenuOpen) {
			setIsMenuOpen(false);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [pathname]);

	const isBackToDashboardButtonPath = ["rating", "profile", "quiz"].some((str) => pathname.includes(str));
	// Don't show if we're there
	const showIdeasLink = loggedIn && path !== "/" + dashboardSlug;

	const BackToDashboardButton = () => {
		if (isBackToDashboardButtonPath) {
			return (
				<Link
					href={`/idea/${params.ideaId}`}
					className="border-1 border-grey group flex translate-y-1 items-center gap-2 rounded-full p-1 px-3 lg:translate-y-2 lg:gap-2.5 lg:p-2 lg:px-4"
				>
					<ChevronRight className="w-1.5 rotate-180 stroke-black" />
					<span className="underline-offset-2 group-hover:underline">Back to dashboard</span>
				</Link>
			);
		}
		return null;
	};

	return (
		<div className="-mb-0.5 flex items-end gap-10 leading-none">
			{loggedIn && !isMobile && <BackToDashboardButton />}
			{isMobile ? (
				<>
					<div className="grid h-full justify-between">
						<button
							className={clsx(
								"z-50",
								isMenuOpen && "text-beige",
								isBackToDashboardButtonPath ? "ml-auto -translate-y-0.5" : "place-self-end",
							)}
							onClick={() => setIsMenuOpen(!isMenuOpen)}
						>
							{isMenuOpen ? "Close" : "Menu"}
						</button>
						<BackToDashboardButton />
					</div>
					<MobileMenu isMenuOpen={isMenuOpen} menu={menu} loggedIn={loggedIn} />
				</>
			) : (
				<div className={clsx("flex flex-col items-end justify-between", !loggedIn && "h-full")}>
					{loggedIn && name ? (
						<div className="mb-3 text-sm italic">Welcome, {name}</div>
					) : (
						<Link
							href="/#get-started"
							scroll={false}
							className="link-underline flex items-center gap-5 font-bold lg:-mt-2"
						>
							Get Started
							<div className="bg-purple grid h-[33px] w-[33px] place-items-center rounded-full">
								<ChevronRight className="stroke-beige h-3 w-3" />
							</div>
						</Link>
					)}

					<div className="flex gap-10">
						{showIdeasLink && (
							<Link href={`/${dashboardSlug}`} className="link-underline">
								Your other ideas
							</Link>
						)}
						{menu &&
							menu.map((item) => (
								<Link key={item?.id} href={item?.href} className="link-underline">
									{item?.text}
								</Link>
							))}

						<LoginButton loggedIn={loggedIn} />
					</div>
				</div>
			)}
		</div>
	);
};

export default Nav;

const LoginButton = ({ loggedIn }: { loggedIn: boolean }) => {
	const { logout } = useSession();
	const router = useRouter();
	if (loggedIn) {
		return (
			<button
				key="logout"
				className="link-underline text-left"
				onClick={async () => {
					await logout();
					router.push("/");
				}}
			>
				Log out
			</button>
		);
	} else {
		return (
			<Link key="login" href="/login" className="link-underline">
				Login
			</Link>
		);
	}
};

type MobileMenuProps = {
	isMenuOpen: boolean;
	menu: GQLInternalLinkBlock[] | GQLExternalLinkBlock[] | null;
	loggedIn: boolean;
};

const MobileMenu = ({ isMenuOpen, menu, loggedIn }: MobileMenuProps) => {
	return (
		<motion.nav
			initial={{ x: "-100%" }}
			animate={{ x: isMenuOpen ? 0 : "-100%" }}
			exit={{ x: "-100%" }}
			transition={{ duration: 0.3 }}
			className="bg-charcoal text-beige fixed left-0 top-0 z-40 h-full w-full"
		>
			<div className="container">
				<div className="border-b-1 border-light flex justify-between py-6">
					<Link href="/">
						<UBOLogo className="fill-beige w-32 lg:w-48" />
						<h1 className="sr-only">Unlocking Business Opportunities</h1>
					</Link>
				</div>
			</div>
			<hr className="bg-hr -z-10 mt-[-1px] w-screen opacity-10" />
			<div className="container">
				<div className="text-36px my-16 flex h-full flex-col leading-normal">
					{loggedIn && <Link href="/dashboard">Dashboard</Link>}
					{menu?.map((item: GQLInternalLinkBlock | GQLExternalLinkBlock) => (
						<Link key={item?.id} href={item?.href}>
							{item?.text}
						</Link>
					))}
					<LoginButton loggedIn={loggedIn} />
				</div>
				<div className="text-24px flex flex-col gap-4">
					<div className="text-16px">We&apos;re here to help</div>
					<Link href="mailto:ask@business.tas.gov.au">ask@business.tas.gov.au</Link>
					<a href="tel:1800 440 026">1800 440 026</a>
				</div>
			</div>
		</motion.nav>
	);
};
